/**
 * `SeedGlyph` — renders the inline SVG children the narrative seeds carry.
 *
 * ## Why this exists
 *
 * `shared/seed/connectors.ts`, `modules.ts` and `loops.ts` each carry an
 * `icon` field holding the demo's own SVG children for a `0 0 24 24` viewBox:
 *
 * ```
 * '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10"/>'
 * ```
 *
 * The demo dropped that string straight into an `<svg>` element. The port
 * cannot: `dangerouslySetInnerHTML` is what `npm run check:oem-handlers`
 * exists to prevent, and it is the wrong tool besides — ported demo content is
 * modelled as typed structures and rendered as JSX, exactly as
 * `shared/assistant/markdown.tsx` does for the assistant's replies.
 *
 * So this component **parses** the string into elements. The grammar is tiny
 * and closed: four self-closing tags (`rect`, `path`, `circle`, `line`) with
 * double-quoted attributes and no children, no text, no namespaces. Anything
 * outside that grammar is dropped rather than guessed at, so a malformed seed
 * loses its glyph instead of breaking the card.
 *
 * ## Why not `ProviderLogo` / `MarketplaceIcon`
 *
 * The design system's two icon components cover AI model providers
 * (`ProviderLogo`: Anthropic, OpenAI, Google, Bedrock…) and marketplace
 * plugins keyed by a remote `logoUrl`. Not one of the twenty-one source
 * systems — Maximo, SAP PM, Pitram, CatBase, XReach, Salesforce — has an entry
 * in either, and `MarketplaceIcon`'s fallback is a hashed-hue letter tile,
 * which would give the connector grid twenty-one different random hues. The
 * demo's own line drawings are both more faithful and quieter, so they are
 * what the port renders.
 *
 * ## Styling
 *
 * Stroke-only, `currentColor`, `stroke-width: 1.5` — the lucide convention the
 * rest of the chrome uses, so a seed glyph sits beside a lucide glyph without
 * reading as a different icon set. The caller owns the colour.
 *
 * UI layer (layer 4 of the 4-layer architecture).
 */

import { useMemo, type ReactElement } from 'react';

/** The four element names the seeds use. Anything else is dropped. */
const ALLOWED_TAGS = new Set(['rect', 'path', 'circle', 'line']);

/**
 * Attributes the seeds set, mapped to the prop name React expects.
 *
 * An attribute outside this set is dropped: the seeds never set a presentation
 * attribute (`fill`, `stroke`, `style`) and nothing should be able to smuggle
 * one in through a seed edit.
 */
const ATTR_PROP: Readonly<Record<string, string>> = {
  d: 'd',
  x: 'x',
  y: 'y',
  x1: 'x1',
  y1: 'y1',
  x2: 'x2',
  y2: 'y2',
  cx: 'cx',
  cy: 'cy',
  r: 'r',
  rx: 'rx',
  ry: 'ry',
  width: 'width',
  height: 'height',
};

/** `<tag attr="value" …/>`, self-closing or not. */
const TAG_RE = /<([a-z]+)\b([^>]*?)\/?>/g;
/** `attr="value"` — double-quoted only, which is how every seed spells it. */
const ATTR_RE = /([a-zA-Z][\w-]*)\s*=\s*"([^"]*)"/g;

/** One parsed element. */
interface GlyphNode {
  readonly tag: string;
  readonly props: Readonly<Record<string, string>>;
}

/**
 * Parse a seed `icon` string into elements.
 *
 * Exported for tests: the parse is the load-bearing part and should be
 * assertable without a renderer.
 */
export function parseGlyph(source: string): GlyphNode[] {
  const nodes: GlyphNode[] = [];
  TAG_RE.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = TAG_RE.exec(source)) !== null) {
    const tag = match[1];
    if (!ALLOWED_TAGS.has(tag)) continue;

    const props: Record<string, string> = {};
    ATTR_RE.lastIndex = 0;
    let attr: RegExpExecArray | null;
    while ((attr = ATTR_RE.exec(match[2])) !== null) {
      const prop = ATTR_PROP[attr[1]];
      if (prop) props[prop] = attr[2];
    }
    if (Object.keys(props).length > 0) nodes.push({ tag, props });
  }

  return nodes;
}

export interface SeedGlyphProps {
  /** The seed's `icon` field — SVG children for a `0 0 24 24` viewBox. */
  icon: string;
  /** Rendered size in px. */
  size?: number;
  className?: string;
}

/** A seed glyph, stroked in `currentColor`. Decorative — never labelled. */
export function SeedGlyph({ icon, size = 18, className }: SeedGlyphProps) {
  const nodes = useMemo(() => parseGlyph(icon), [icon]);
  if (nodes.length === 0) return null;

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {nodes.map((node, i) => renderNode(node, i))}
    </svg>
  );
}

function renderNode(node: GlyphNode, index: number): ReactElement | null {
  const key = `${node.tag}-${index}`;
  switch (node.tag) {
    case 'rect':
      return <rect key={key} {...node.props} />;
    case 'path':
      return <path key={key} {...node.props} />;
    case 'circle':
      return <circle key={key} {...node.props} />;
    case 'line':
      return <line key={key} {...node.props} />;
    default:
      return null;
  }
}
